import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Field, Mono, PageBody, PageHeader, StackedList } from "../ui/Field";

type S = { id: string; key: string; name: string; draft_revision: number; live_revision: number | null };

export default function Snippets() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState<S[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [key, setKey] = useState("");
  const [name, setName] = useState("");

  const load = () => api.get<{ items: S[] }>(`/api/sites/${slug}/snippets`).then((r) => setItems(r.items)).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    try {
      const r = await api.post<{ id: string }>(`/api/sites/${slug}/snippets`, { key, name, blocks: [] });
      nav(`/sites/${slug}/snippets/${r.id}`);
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 04 · Reusable blocks"
        title={<>Snippets</>}
        description="Reusable groups of blocks you can drop into any page — banners, CTAs, footers, signposts."
        actions={<Button onClick={() => setShowNew((s) => !s)}>{showNew ? "Cancel" : "+ Snippet"}</Button>}
      />
      {err && <Banner kind="error">{err}</Banner>}

      {showNew && (
        <Card number="N0" title="New snippet" className="mb-6">
          <form onSubmit={create}>
            <Field label="Key" required value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-"))} placeholder="cta-banner" help="Lowercase, hyphenated identifier used in code." />
            <Field label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="CTA banner" />
            <Button type="submit">Create</Button>
          </form>
        </Card>
      )}

      <Card number="N1" title={`Library (${items.length})`} bodyClassName={items.length === 0 ? "p-5" : "p-0"}>
        {items.length === 0 ? (
          <EmptyState
            title="No snippets yet."
            description="Snippets are reusable groups of blocks you can drop into any page."
            action={<Button onClick={() => setShowNew(true)}>+ Create snippet</Button>}
          />
        ) : (
          <StackedList className="px-5">
            {items.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 py-3.5">
                <Link to={`/sites/${slug}/snippets/${s.id}`} className="flex-1 no-underline group">
                  <span className="block text-[14px] text-[var(--color-ink)] group-hover:text-[var(--color-accent)] transition-colors">
                    {s.name} <Mono className="text-[12px] ml-1">· {s.key}</Mono>
                  </span>
                  <span className="block text-[11px] text-[var(--color-muted)] font-mono mt-0.5">
                    draft {s.draft_revision}{s.live_revision ? ` · live ${s.live_revision}` : " · not published"}
                  </span>
                </Link>
                <Badge tone={s.live_revision ? "success" : "neutral"}>{s.live_revision ? "published" : "draft"}</Badge>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
